import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { SPENDING_CATEGORIES } from '../data/spendingCategories';
import { useModalA11y } from '../utils/useModalA11y';

function ManualTransactionModal({ isOpen, onClose }) {
    const { addManualTransaction, isSampleMode, triggerSampleLocked, setStatus } = useAppContext();
    const [txnType, setTxnType] = useState('DEBIT');
    const [amount, setAmount] = useState('');
    const [party, setParty] = useState('');
    const [category, setCategory] = useState('');
    const [subcategory, setSubcategory] = useState('');
    const [date, setDate] = useState('');

    useModalA11y({ onClose, isOpen });

    useEffect(() => {
        if (isOpen) {
            setDate(new Date().toISOString().split('T')[0]);
        }
    }, [isOpen]);

    const handleSave = async () => {
        if (isSampleMode) { triggerSampleLocked(); onClose(); return; }
        const amt = parseFloat(amount);
        if (!amt || !party.trim() || !category) {
            setStatus?.({ active: true, text: 'Fill amount, party, and category', type: 'error' });
            return;
        }

        const txn = {
            date_time: date + ' ' + new Date().toLocaleTimeString(),
            amount: amt.toString(),
            type: txnType,
            party: party.trim(),
            category: category,
            subcategory: subcategory,
            source: 'manual',
            tag: category
        };

        await addManualTransaction(txn);
        onClose();
        setStatus?.({ active: true, text: 'Manual spend added — counted in totals', type: 'success' });

        setAmount('');
        setParty('');
        setCategory('');
        setSubcategory('');
    };

    const subcategories = category && SPENDING_CATEGORIES[category]
        ? Object.keys(SPENDING_CATEGORIES[category]).sort()
        : [];

    if (!isOpen) return null;

    return (
        <div
            className="modal-overlay active"
            role="presentation"
            onClick={onClose}
        >
            <div
                className="modal-card"
                role="dialog"
                aria-modal="true"
                aria-labelledby="manual-txn-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-header">
                    <div className="modal-title" id="manual-txn-title">Add Transaction</div>
                    <button type="button" className="close-btn" onClick={onClose} aria-label="Close">&times;</button>
                </div>

                <div className="type-togglers" role="group" aria-label="Transaction type">
                    <button
                        type="button"
                        className={`type-btn ${txnType === 'DEBIT' ? 'active debit' : ''}`}
                        onClick={() => setTxnType('DEBIT')}
                        aria-pressed={txnType === 'DEBIT'}
                    >
                        Debit
                    </button>
                    <button
                        type="button"
                        className={`type-btn ${txnType === 'CREDIT' ? 'active credit' : ''}`}
                        onClick={() => setTxnType('CREDIT')}
                        aria-pressed={txnType === 'CREDIT'}
                    >
                        Credit
                    </button>
                </div>

                <div className="form-group">
                    <label className="form-label" htmlFor="manual-amount">Amount</label>
                    <input
                        id="manual-amount"
                        type="number"
                        inputMode="decimal"
                        className="form-input"
                        placeholder="0.00"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                    />
                </div>

                <div className="form-group">
                    <label className="form-label" htmlFor="manual-party">Party / Merchant</label>
                    <input
                        id="manual-party"
                        type="text"
                        className="form-input"
                        placeholder="e.g. Starbucks, Uber"
                        value={party}
                        onChange={(e) => setParty(e.target.value)}
                    />
                </div>

                <div className="form-group">
                    <label className="form-label" htmlFor="manual-category">Category</label>
                    <select
                        id="manual-category"
                        className="form-select"
                        value={category}
                        onChange={(e) => { setCategory(e.target.value); setSubcategory(''); }}
                    >
                        <option value="">Select Category</option>
                        {Object.keys(SPENDING_CATEGORIES).sort().map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label className="form-label" htmlFor="manual-subcategory">Subcategory</label>
                    <select
                        id="manual-subcategory"
                        className="form-select"
                        value={subcategory}
                        onChange={(e) => setSubcategory(e.target.value)}
                    >
                        <option value="">Select Subcategory</option>
                        {subcategories.map(sub => (
                            <option key={sub} value={sub}>{sub}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label className="form-label" htmlFor="manual-date">Date</label>
                    <input
                        id="manual-date"
                        type="date"
                        className="form-input"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                    />
                </div>

                <button type="button" className="submit-btn" onClick={handleSave}>Add Transaction</button>
            </div>
        </div>
    );
}

export default ManualTransactionModal;
